import type { ListBuilderColumn } from '../../types';

/**
 * Item # column showing the best available identifier:
 * externalId2 if present, else externalId, else the database _id.
 * The displayItemNumber field is computed in ClientItemHistory.tsx.
 */
export const createItemNumberColumn = (): ListBuilderColumn => ({
	id: 'itemNumber',
	fields: [
		{
			id: 'item-number-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'displayItemNumber' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-item-number', text: 'Item #' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Shared item column definition with link to item page
 */
export const createItemColumn = (): ListBuilderColumn => ({
	id: 'item',
	fields: [
		{
			id: 'name-field',
			link: {
				type: 'object',
				enabled: true,
				idField: 'itemId',
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'itemName', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'category-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'category' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-category',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'manufacturer-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'manufacturer', objectTable: 'item' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h1', text: 'Item' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'medium' },
	verticalAlignment: 'middle',
});

/**
 * Item column with custom category field ID suffix
 * Used in due-for-reorder and at-risk lists
 */
export const createItemColumnWithSuffix = (
	suffix: string,
): ListBuilderColumn => ({
	id: 'item',
	fields: [
		{
			id: 'name-field',
			link: {
				type: 'object',
				enabled: true,
				idField: 'itemId',
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'itemName', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: `category-field-${suffix}`,
			dataConfig: {
				type: 'field',
				field: { fieldName: 'category' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: `line-break-category-${suffix}`,
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'manufacturer-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'manufacturer', objectTable: 'item' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h1', text: 'Item' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'medium' },
	verticalAlignment: 'middle',
});

/**
 * Shared chart column definition
 */
export const createChartColumn = (idSuffix: string): ListBuilderColumn => ({
	id: `chart${idSuffix}`,
	fields: [
		{
			id: `chart${idSuffix.toLowerCase()}-field`,
			dataConfig: {
				type: 'field',
				field: { fieldName: 'monthlyData' },
				defaultValue: '[]',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'client-item-history-trend-chart' },
		},
	],
	headers: [{ id: `h-chart${idSuffix.toLowerCase()}`, text: '12 Month Trend' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'middle',
});

/**
 * Basic chart column without suffix (for previously purchased list)
 */
export const createBasicChartColumn = (): ListBuilderColumn => ({
	id: 'chart',
	fields: [
		{
			id: 'chart-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'monthlyData' },
				defaultValue: '[]',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'client-item-history-trend-chart' },
		},
	],
	headers: [{ id: 'h4', text: '12 Month Trend' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'middle',
});

/**
 * Purchase info column — renders date (X days ago), frequency, and invoice link
 * via the client-item-history-purchase-info custom component.
 * Used by all three tabs (Previously Purchased, Reorder, At Risk).
 */
export const createPurchaseInfoColumn = (): ListBuilderColumn => ({
	id: 'purchaseInfo',
	fields: [
		{
			id: 'purchase-info-custom',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastPurchasedDate' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'custom',
			customComponent: { id: 'client-item-history-purchase-info' },
		},
	],
	headers: [
		{ id: 'h-last-purchased', text: 'Last Purchased' },
		{ id: 'h-frequency', text: 'Frequency' },
		{ id: 'h-last-invoice', text: 'Last Invoice' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'small' },
	verticalAlignment: 'middle',
});

/**
 * Last sale column for previously purchased list (PD-14018). Replaces the
 * old Total Spent / Avg Unit Price stack — the average weighted every
 * invoice line equally so it was misleading, and "what did they last pay,
 * for how many" is the figure a rep actually pitches with.
 */
export const createLastPriceColumn = (): ListBuilderColumn => ({
	id: 'lastPrice',
	fields: [
		{
			id: 'last-price-sold-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastPriceSold' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
		{
			id: 'line-break-last-price',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'last-quantity-sold-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastQuantitySold' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'number',
		},
	],
	headers: [
		{ id: 'h-last-price', text: 'Last Price' },
		{ id: 'h-last-qty', text: 'Last Qty' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Average per order column for previously purchased list
 */
export const createAvgPerOrderColumn = (): ListBuilderColumn => ({
	id: 'avgPerOrder',
	fields: [
		{
			id: 'avg-qty-per-order-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgQtyPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				formatOptions: { decimalPlaces: 1 },
			},
			displayStyle: 'number',
		},
		{
			id: 'line-break-avg-order',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'avg-sales-per-order-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgSalesPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
		{
			id: 'line-break-avg-unit',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			// PD-14018 follow-up (per Chris): keep an average unit price in the
			// Avg stack - now quantity-weighted (SUM(total)/SUM(qty)), fixing
			// the original AVG(price/qty) math instead of dropping the stat.
			id: 'avg-unit-price-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgUnitPrice' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h-avg-order-1', text: 'Avg Qty/Order' },
		{ id: 'h-avg-order-2', text: 'Avg $/Order' },
		{ id: 'h-avg-order-3', text: 'Avg Unit Price' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Reorder info column for due-for-reorder list
 */
export const createReorderInfoColumn = (): ListBuilderColumn => ({
	id: 'reorderInfo',
	fields: [
		{
			id: 'avg-interval-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgPurchaseIntervalDays' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-reorder-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'days-since-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'daysSinceLastPurchase' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-reorder-2',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'days-overdue-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'daysOverdue' },
			},
			styleConfig: {
				size: 'xs',
				// A red highlight for daysOverdue > 0 was configured here but
				// never fired: the color-rule engine only supports
				// equals/not_equals/in/not_in and reads field/operator at the
				// top level of each rule. Kept the observed behavior (always
				// the gray default); a numeric threshold needs a range-mapping
				// rule if the highlight is wanted.
				colorRule: {
					type: 'conditional',
					rules: [],
					defaultColor: { themeColor: 'textMuted' },
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h-reorder-1', text: 'Avg. Interval (days)' },
		{ id: 'h-reorder-2', text: 'Days Since Purchase' },
		{ id: 'h-reorder-3', text: 'Days Overdue' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'medium' },
	verticalAlignment: 'middle',
});

/**
 * Purchase info column for due-for-reorder list — delegates to shared createPurchaseInfoColumn
 */
export const createReorderPurchaseInfoColumn = (): ListBuilderColumn =>
	createPurchaseInfoColumn();

/**
 * Average per purchase column for due-for-reorder list
 */
export const createAvgPerPurchaseColumn = (): ListBuilderColumn => ({
	id: 'avgPerPurchase',
	fields: [
		{
			id: 'avg-qty-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgQtyPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				formatOptions: { decimalPlaces: 1 },
			},
			displayStyle: 'number',
		},
		{
			id: 'line-break-avg-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'avg-sales-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgSalesPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h-avg-1', text: 'Avg Qty/Order' },
		{ id: 'h-avg-2', text: 'Avg $/Order' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * 12-month totals column for due-for-reorder list (PD-14018: the Last/Prev
 * 6-month sub-rows were dropped — the trend chips and sparkline already
 * carry that signal).
 */
export const createReorderTotalsColumn = (): ListBuilderColumn => ({
	id: 'totalsReorder',
	fields: [
		{
			id: 'twelve-month-qty-reorder-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'quantityLast12Months' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-twelve-month-reorder',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'twelve-month-sales-reorder-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'totalSpent' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h-twelve-month-qty-reorder', text: '12-Mo Qty' },
		{ id: 'h-twelve-month-sales-reorder', text: '12-Mo Sales' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Purchase info column for at-risk list — delegates to shared createPurchaseInfoColumn
 */
export const createAtRiskPurchaseInfoColumn = (): ListBuilderColumn =>
	createPurchaseInfoColumn();

/**
 * Quantities column for at-risk list (PD-14018: 6-month sub-rows dropped —
 * the trend chips and sparkline already carry that signal).
 */
export const createAtRiskQuantitiesColumn = (): ListBuilderColumn => ({
	id: 'quantities',
	fields: [
		{
			id: 'twelve-month-qty-atrisk-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'quantityLast12Months' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-twelve-mo-qty-atrisk', text: '12-Mo Qty' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Average per order column for at-risk list
 */
export const createAtRiskAvgPerOrderColumn = (): ListBuilderColumn => ({
	id: 'avgPerOrderAtRisk',
	fields: [
		{
			id: 'avg-qty-atrisk-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgQtyPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				formatOptions: { decimalPlaces: 1 },
			},
			displayStyle: 'number',
		},
		{
			id: 'line-break-avg-atrisk',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'avg-sales-atrisk-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'avgSalesPerPurchase' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h-avg-atrisk-1', text: 'Avg Qty/Order' },
		{ id: 'h-avg-atrisk-2', text: 'Avg $/Order' },
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Rank column for the Recommended list (PD-14017) — the item's 1-based
 * position in the account's precomputed recommendation list (1 = best).
 * The top three ranks get medal tints (gold/silver/bronze) so the head of
 * the list reads as earned; everything below stays plain.
 */
export const createRecommendationRankColumn = (): ListBuilderColumn => ({
	id: 'recommendationRank',
	fields: [
		{
			id: 'recommendation-rank-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'rank' },
			},
			styleConfig: {
				size: 'sm',
				// The top three used to take gold / silver / bronze tints. A rank
				// is ordinal, not a success/warning/danger reading, so there is
				// no meaning for the token layer to colour it with — and three
				// medal hues are three colours that exist nowhere else in the
				// product. The rank reads as a number, in order, which is what a
				// rank is; the semibold weight keeps the column scannable.
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				fontWeight: 'semibold',
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-recommendation-rank', text: '#' }],
	sortable: false,
	alignment: 'center',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Product image column for the Recommended list (PD-14017) — the same
 * thumbnail treatment the Shop uses (primaryImageUrl is enriched by the
 * recommended-items read path).
 */
export const createRecommendationImageColumn = (): ListBuilderColumn => ({
	id: 'recommendationImage',
	fields: [
		{
			id: 'recommendation-image-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'primaryImageUrl', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				imageOptions: {
					size: 'small',
					enableViewer: true,
				},
			},
			displayStyle: 'image',
		},
	],
	headers: [{ id: 'h-recommendation-image', text: '' }],
	sortable: false,
	alignment: 'center',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Strength column for the Recommended list (PD-14017) — every row shows its
 * score relative to the account's strongest recommendation of the same
 * reason family (affinity for Bought-together rows, buyer reach for Popular
 * rows). Range-tinted: strong green, solid blue, weak gray. Headed
 * "Strength" rather than "Match" because popularity reach isn't an
 * account-fit claim.
 */
export const createRecommendationMatchColumn = (): ListBuilderColumn => ({
	id: 'recommendationMatch',
	fields: [
		{
			id: 'recommendation-match-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'matchPercent' },
				defaultValue: '',
			},
			suffix: '%',
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'range-mapping',
					rangeMapping: [
						{ min: 80, max: 100, color: { themeColor: 'success' } }, // green-700
						{ min: 50, max: 79, color: { themeColor: 'statusInfo' } }, // blue-700
						{ min: 1, max: 49, color: { themeColor: 'textSecondary' } }, // gray-500
					],
				},
				fontWeight: 'semibold',
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-recommendation-match', text: 'Strength' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});

/**
 * Reason column for the Recommended list (PD-14017) — replaces the old
 * five-star factor grid. ONE self-contained chip per row (review feedback:
 * no chip-plus-phrase): "Bought together" / "Popular in TX" / "Similar
 * accounts" — text computed in ClientItemHistory.tsx, color keyed off
 * reasonCode so the type reads at a glance. COPURCHASE rows additionally
 * show the anchor item beneath as a LINK to that item — "bought together
 * with WHAT" is the actionable part, and an item name doesn't fit a chip.
 * The full sentence (recommendationReason) stays in the API payload for
 * other consumers (AI tools, future surfaces).
 */
export const createRecommendationReasonColumn = (): ListBuilderColumn => ({
	id: 'recommendationReason',
	fields: [
		{
			id: 'recommendation-reason-chip-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'reasonChip' },
			},
			styleConfig: {
				size: 'xs',
				// Color by reason TYPE (the chip text varies per row, so the
				// color rides on reasonCode via conditional rules). Teal
				// SIMILAR_ACCOUNTS is pre-wired for the PD-14088 lookalike layer.
				colorRule: {
					type: 'conditional',
					// NOTE: the engine reads rule.field.fieldName (nested), not a
					// flat fieldName — see evaluateColorCondition in colorRuleEngine.
					rules: [
						{
							field: { fieldName: 'reasonCode' },
							operator: 'equals',
							value: 'COPURCHASE', // indigo
						},
						{
							field: { fieldName: 'reasonCode' },
							operator: 'equals',
							value: 'SIMILAR_ACCOUNTS', // teal
						},
					],
					// Amber for popularity rows. Tints are one shade deeper than the
					// usual -50 palette so the pill reads on white table rows.
					defaultColor: { type: 'warning' },
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'recommendation-reason-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			// COPURCHASE only: the anchor item from the account's own history,
			// linked to the item record so "with what?" is one click away.
			// Empty (renders nothing) for popularity rows.
			id: 'recommendation-reason-anchor-field',
			link: {
				type: 'object',
				enabled: true,
				idField: 'topAnchorItemId',
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'reasonSecondary' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-recommendation-reason', text: 'Why Recommended' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible' },
	verticalAlignment: 'middle',
});

/**
 * Price column for the Recommended list (PD-14017) — the item's current
 * list price (these are not-yet-purchased items, so there is no "last
 * price" to show).
 */
export const createRecommendationPriceColumn = (): ListBuilderColumn => ({
	id: 'recommendationPrice',
	fields: [
		{
			id: 'recommendation-price-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'price' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [{ id: 'h-recommendation-price', text: 'Price' }],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'fixed', preset: 'xsmall' },
	verticalAlignment: 'middle',
});
